import React, { useEffect, useRef, useState } from "react";
import Layout from "../components/app/Layout";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../contexts/I18nContext";
import api, { API_BASE } from "../lib/api";
import { authImageUrl } from "../lib/storage";
import { Send, Plus, X, Paperclip, Image as ImageIcon, FileText, Film, Sparkles, Lock, Mic, ArrowLeft, Check, ShieldCheck, Hourglass } from "lucide-react";
import VoiceRecorder from "../components/app/VoiceRecorder";
import ReportButton from "../components/app/ReportButton";

const SPECIALTY_MAP = {
  general: { bg: "Футболен", en: "Football" },
  goalkeeper: { bg: "Вратарски", en: "Goalkeeper" },
  fitness: { bg: "Фитнес", en: "Fitness" },
  technical: { bg: "Технически", en: "Technical" },
  defense: { bg: "Защита", en: "Defense" },
  attack: { bg: "Нападение", en: "Attack" },
  youth: { bg: "Деца", en: "Youth" },
};
const specialtyLabel = (s, lang) => SPECIALTY_MAP[s]?.[lang === "bg" ? "bg" : "en"] || "";

function AttachmentPreview({ a, mine }) {
  const url = authImageUrl(a.url || a.path);
  if (a.kind === "voice" || (a.content_type || "").startsWith("audio/")) {
    return (
      <div className={`bubble ${mine ? "bubble-sent" : "bubble-received"} bubble-voice flex-col items-stretch`} style={{ maxWidth: 300 }}>
        <div className="flex items-center gap-2.5">
          <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${mine ? "bg-black/15" : "bg-pitch/15"}`}>
            <Mic className={`w-4 h-4 ${mine ? "text-black" : "text-pitch"}`} />
          </div>
          <span className="waveform waveform-static flex-1">
            <i style={{ height: 10 }} /><i style={{ height: 16 }} /><i style={{ height: 7 }} /><i style={{ height: 14 }} /><i style={{ height: 9 }} /><i style={{ height: 12 }} /><i style={{ height: 6 }} />
          </span>
          <audio src={url} controls className="hidden" />
        </div>
        <audio src={url} controls className="h-8 w-full mt-2" />
        {a.transcription && (
          <div className={`text-xs italic mt-2 pt-2 border-t ${mine ? "border-black/15 text-black/75" : "border-white/8 text-white/70"}`}>
            "{a.transcription}"
            {a.language && <span className={`ml-1 text-[10px] font-mono uppercase ${mine ? "text-black/50" : "text-white/35"}`}>[{a.language}]</span>}
          </div>
        )}
      </div>
    );
  }
  if (a.kind === "image" || (a.content_type || "").startsWith("image/")) {
    return <img src={url} alt={a.name} className="max-w-[240px] max-h-[240px] object-cover border border-white/10 rounded-lg" style={{ borderRadius: 14 }} />;
  }
  if (a.kind === "video" || (a.content_type || "").startsWith("video/")) {
    return <video src={url} controls className="max-w-[280px] max-h-[280px] rounded-lg" style={{ borderRadius: 14 }} />;
  }
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className={`bubble ${mine ? "bubble-sent" : "bubble-received"} flex items-center gap-2`}>
      <FileText className={`w-4 h-4 ${mine ? "text-black" : "text-pitch"}`} />
      <span className="text-xs underline truncate max-w-[180px]">{a.name || "file"}</span>
    </a>
  );
}

function PendingChatBanner({ active, user, lang, onApproved, onRequestSent }) {
  const isInitiator = active.initiator_id === user.id;
  const isRecipient = !isInitiator;
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [busy, setBusy] = useState(false);

  const sendRequest = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/chat/threads/${active.id}/request`, { content: text.trim() });
      onRequestSent?.({ ...active, status: data.status || "pending" });
      setText("");
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to send request");
    } finally {
      setSending(false);
    }
  };

  const approve = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/chat/threads/${active.id}/approve`);
      onApproved?.({ ...active, status: data.status || "open" });
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to approve");
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    if (!window.confirm(lang === "bg" ? "Откажи заявката?" : "Decline this request?")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/chat/threads/${active.id}/reject`);
      onApproved?.({ ...active, status: data.status || "rejected" });
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to reject");
    } finally {
      setBusy(false);
    }
  };

  // Coach/Academy side — Approve / Reject
  if (isRecipient) {
    return (
      <div className="flex-shrink-0 border-b border-pitch/20 bg-pitch/5 px-4 py-3" data-testid="chat-pending-banner-recipient">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-full bg-pitch/15 border border-pitch/40 flex items-center justify-center flex-shrink-0">
            <ShieldCheck className="w-4 h-4 text-pitch" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-oswald uppercase tracking-tight text-sm text-pitch">
              {lang === "bg" ? "Нова заявка за тренировка" : "New training request"}
            </div>
            <div className="text-[11px] font-mono text-white/55 mt-0.5">
              {lang === "bg"
                ? `${active.other_user?.name || "Потребител"} иска да тренира с теб. Одобри, за да отключиш чата.`
                : `${active.other_user?.name || "User"} wants to train with you. Approve to unlock chat.`}
            </div>
            <div className="flex gap-2 mt-2.5">
              <button
                onClick={approve}
                disabled={busy}
                data-testid="chat-approve-request"
                className="btn-primary text-[11px] tracking-widest uppercase px-3 py-1.5 flex items-center gap-1.5 disabled:opacity-50"
              >
                <Check className="w-3.5 h-3.5" /> {lang === "bg" ? "Одобри" : "Approve"}
              </button>
              <button
                onClick={reject}
                disabled={busy}
                data-testid="chat-reject-request"
                className="text-[11px] tracking-widest uppercase px-3 py-1.5 border border-white/15 text-white/70 hover:border-red-400 hover:text-red-300 transition-colors disabled:opacity-50"
              >
                <X className="w-3.5 h-3.5 inline-block mr-1" /> {lang === "bg" ? "Откажи" : "Decline"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Initiator side — Awaiting approval
  return (
    <div className="flex-shrink-0 border-b border-amber-400/20 bg-amber-400/5 px-4 py-3" data-testid="chat-pending-banner-initiator">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-full bg-amber-400/15 border border-amber-400/40 flex items-center justify-center flex-shrink-0">
          <Hourglass className="w-4 h-4 text-amber-300" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-oswald uppercase tracking-tight text-sm text-amber-200">
            {lang === "bg" ? "Очаква одобрение" : "Awaiting approval"}
          </div>
          <div className="text-[11px] font-mono text-white/55 mt-0.5">
            {lang === "bg"
              ? "Изпрати кратко съобщение, за да поискаш тренировка. Чатът ще се отвори, щом треньорът одобри."
              : "Send a short request to train. The chat unlocks once the coach approves."}
          </div>
          <div className="flex gap-2 mt-2.5">
            <input
              type="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              maxLength={300}
              placeholder={lang === "bg" ? "Здравей! Бих искал да тренирам с теб." : "Hi! I'd like to train with you."}
              className="flex-1 bg-[#0A1628] border border-white/10 px-3 py-1.5 focus:border-pitch outline-none rounded-full text-xs"
              data-testid="chat-request-input"
            />
            <button
              onClick={sendRequest}
              disabled={sending}
              className="btn-primary text-[11px] tracking-widest uppercase px-3 py-1.5 flex items-center gap-1.5 disabled:opacity-50"
              data-testid="chat-send-request"
            >
              <Send className="w-3.5 h-3.5" /> {lang === "bg" ? "Изпрати" : "Send"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


export default function ChatPage() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [users, setUsers] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [online, setOnline] = useState(new Set());
  const fileRef = useRef(null);
  const wsRef = useRef(null);
  const scrollRef = useRef(null);

  const loadThreads = async () => {
    const { data } = await api.get("/chat/threads");
    setThreads(data);
    return data;
  };

  const loadPresence = async () => {
    try {
      const { data } = await api.get("/presence");
      setOnline(new Set(data.online || []));
    } catch (_) {}
  };

  useEffect(() => { loadThreads(); loadPresence(); const t = setInterval(loadPresence, 25000); return () => clearInterval(t); }, []);

  useEffect(() => {
    const token = localStorage.getItem("auth_token");
    if (!token) return;
    const wsUrl = API_BASE.replace(/^http/, "ws") + `/ws/chat?token=${encodeURIComponent(token)}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        if (msg.type === "message") {
          setMessages((prev) => {
            if (active && msg.data.thread_id === active.id) {
              if (prev.find((m) => m.id === msg.data.id)) return prev;
              return [...prev, msg.data];
            }
            return prev;
          });
          loadThreads();
        }
      } catch (_) {}
    };
    return () => { try { ws.close(); } catch (_) {} };
    // eslint-disable-next-line
  }, [active]);

  const openThread = async (th) => {
    setActive(th);
    const { data } = await api.get(`/chat/threads/${th.id}/messages`);
    setMessages(data);
  };

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if ((!text.trim() && pendingFiles.length === 0) || !active) return;
    const { data } = await api.post(`/chat/threads/${active.id}/messages`, {
      thread_id: active.id,
      content: text.trim(),
      attachments: pendingFiles,
    });
    setMessages((prev) => prev.find((m) => m.id === data.id) ? prev : [...prev, data]);
    setText("");
    setPendingFiles([]);
  };

  const openNew = async () => {
    const role = user?.role === "coach" ? "parent" : "coach";
    const { data } = await api.get("/users", { params: { role } });
    setUsers(data);
    setShowNew(true);
  };

  const createThread = async (otherId) => {
    const { data } = await api.post("/chat/threads", { other_user_id: otherId });
    setShowNew(false);
    await loadThreads();
    openThread(data);
  };

  const handleFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    const out = [];
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await api.post("/uploads/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
        out.push(data);
      } catch (ex) {
        const detail = ex.response?.data?.detail || "Upload failed";
        alert(detail);
      }
    }
    setPendingFiles((p) => [...p, ...out]);
    setUploading(false);
    e.target.value = "";
  };

  const removePending = (idx) => setPendingFiles((p) => p.filter((_, i) => i !== idx));

  return (
    <Layout>
      <div className="otp-chat h-[calc(100dvh-5rem)] md:h-[calc(100dvh-7rem)] grid grid-cols-1 md:grid-cols-[320px_1fr] gap-3 md:gap-4">
        {/* Threads list */}
        <aside className={`card-sharp flex flex-col overflow-hidden ${active ? "hidden md:flex" : "flex"}`}>
          <div className="p-4 border-b border-white/10 flex items-center justify-between">
            <h2 className="font-oswald uppercase text-xl">{t("messages")}</h2>
            <button onClick={openNew} className="btn-primary p-2" data-testid="btn-new-chat"><Plus className="w-4 h-4" /></button>
          </div>
          <div className="overflow-y-auto flex-1 scroll-thin">
            {threads.length === 0 ? (
              <div className="p-6 text-white/30 text-xs font-mono tracking-widest text-center">{t("noMessages")}</div>
            ) : threads.map((th) => (
              <button key={th.id} onClick={() => openThread(th)} className={`w-full text-left px-4 py-3 border-b border-white/5 hover:bg-white/5 transition-all ${active?.id === th.id ? "bg-white/5 border-l-2 border-l-pitch" : ""}`} data-testid={`thread-${th.id}`} data-thread-item="true">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    {th.other_user?.photo_url ? (
                      <img
                        src={th.other_user.photo_url + (th.other_user.photo_url.startsWith("/api") ? `?auth=${localStorage.getItem("auth_token")}` : "")}
                        alt={th.other_user?.name}
                        className="w-11 h-11 rounded-full object-cover border-2 border-pitch/30"
                        onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextElementSibling.style.display = "flex"; }}
                      />
                    ) : null}
                    <div className={`w-11 h-11 rounded-full bg-pitch text-white font-oswald flex items-center justify-center text-base avatar-ring ${th.other_user?.photo_url ? "hidden" : "flex"}`}>
                      {th.other_user?.name?.[0]}
                    </div>
                    {online.has(th.other_user?.id) && (
                      <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-pitch border-2 border-[#0A1628]" title="Online" style={{ background: "#3B82F6", boxShadow: "0 0 6px #3B82F6" }} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="font-oswald uppercase tracking-tight truncate text-sm">{th.other_user?.name}</div>
                      <span className="text-[9px] font-mono text-white/35 uppercase tracking-widest">
                        {th.other_user?.role === "coach" ? (lang === "bg" ? "Тр" : "C")
                          : th.other_user?.role === "parent" ? (lang === "bg" ? "Р" : "P")
                          : th.other_user?.role === "academy" ? (lang === "bg" ? "Ак" : "A")
                          : (lang === "bg" ? "Иг" : "Pl")}
                      </span>
                    </div>
                    <div className="text-xs text-white/40 truncate font-mono">
                      {th.last_message?.content || (th.last_message?.attachments?.length ? "📎 attachment" : "—")}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </aside>

        {/* Message panel */}
        <section className={`card-sharp flex flex-col overflow-hidden ${active ? "flex" : "hidden md:flex"}`}>
          {active ? (
            <>
              {/* Header — sticky-feel via flex-shrink-0 */}
              <div className="flex-shrink-0 header-blur px-4 py-3 flex items-center gap-3 border-b border-white/8">
                <button className="md:hidden p-1.5 -ml-1.5 text-white/55 hover:text-white" onClick={() => setActive(null)} data-testid="back-threads" aria-label="Back">
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <div className="relative">
                  {active.other_user?.photo_url ? (
                    <img
                      src={active.other_user.photo_url + (active.other_user.photo_url.startsWith("/api") ? `?auth=${localStorage.getItem("auth_token")}` : "")}
                      alt={active.other_user?.name}
                      className="w-11 h-11 rounded-full object-cover border-2 border-pitch/40"
                      onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextElementSibling.style.display = "flex"; }}
                    />
                  ) : null}
                  <div className={`w-11 h-11 rounded-full bg-pitch text-white font-oswald flex items-center justify-center avatar-ring ${active.other_user?.photo_url ? "hidden" : "flex"}`}>
                    {active.other_user?.name?.[0]}
                  </div>
                  {online.has(active.other_user?.id) && (
                    <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-pitch border-2 border-[#0A1628]" title="Online" style={{ background: "#3B82F6", boxShadow: "0 0 8px #3B82F6" }} data-testid="online-dot" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-oswald uppercase tracking-tight truncate text-lg leading-none" data-testid="chat-header-name">{active.other_user?.name}</div>
                  <div className="text-[11px] text-white/55 font-mono flex items-center gap-1.5 mt-1">
                    {online.has(active.other_user?.id)
                      ? <><span className="inline-block w-1.5 h-1.5 rounded-full bg-pitch" /> {lang === "bg" ? "На линия" : "Online"}</>
                      : <><span className="inline-block w-1.5 h-1.5 rounded-full bg-white/20" /> {lang === "bg" ? "Извън линия" : "Offline"}</>}
                    <span className="text-white/20">·</span>
                    <span className="uppercase tracking-widest text-[10px]">
                      {active.other_user?.role === "coach" && (lang === "bg" ? "Треньор" : "Coach")}
                      {active.other_user?.role === "parent" && (lang === "bg" ? "Родител" : "Parent")}
                      {active.other_user?.role === "player" && (lang === "bg" ? "Играч" : "Player")}
                      {active.other_user?.role === "academy" && (lang === "bg" ? "Академия" : "Academy")}
                    </span>
                  </div>
                </div>
                <ReportButton reportedId={active.other_user?.id} reportedName={active.other_user?.name} context="chat" contextId={active.id} variant="icon" testIdSuffix="chat" />
              </div>

              {/* Iter 35 — Chat approval banner */}
              {active.status === "pending" && (
                <PendingChatBanner
                  active={active}
                  user={user}
                  lang={lang}
                  onApproved={(updated) => { setActive(updated); loadThreads(); }}
                  onRequestSent={(updated) => { setActive(updated); loadThreads(); }}
                />
              )}
              {active.status === "rejected" && (
                <div className="flex-shrink-0 border-b border-white/8 px-4 py-3 bg-red-500/5 text-[11px] font-mono text-red-300" data-testid="chat-status-rejected">
                  {lang === "bg" ? "Заявката беше отказана." : "This request was declined."}
                </div>
              )}

              {/* Scrollable message stream — flex-1 + min-h-0 for proper shrink */}
              <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-3 md:px-5 py-4 space-y-1 scroll-thin" data-testid="chat-stream">
                {messages.length === 0 && (
                  <div className="h-full flex flex-col items-center justify-center text-white/30">
                    <div className="w-14 h-14 rounded-full bg-pitch/10 border border-pitch/30 flex items-center justify-center mb-3">
                      <Send className="w-5 h-5 text-pitch" />
                    </div>
                    <div className="font-oswald uppercase text-base tracking-tight mb-1">{lang === "bg" ? "Започни разговор" : "Start the conversation"}</div>
                    <div className="text-[10px] font-mono tracking-widest text-white/35">{t("noMessages")}</div>
                  </div>
                )}
                {messages.map((m, i) => {
                  const mine = m.sender_id === user.id;
                  const prev = messages[i - 1];
                  const grouped = prev && prev.sender_id === m.sender_id && (new Date(m.created_at) - new Date(prev.created_at) < 60000);
                  const showAvatar = !mine && !grouped;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"} ${grouped ? "mt-0.5" : "mt-3"} items-end gap-2`} data-testid={`msg-${m.id}`}>
                      {!mine && (
                        showAvatar ? (
                          <div className="w-7 h-7 rounded-full bg-pitch/15 text-pitch font-oswald flex items-center justify-center text-xs flex-shrink-0">
                            {active.other_user?.name?.[0]}
                          </div>
                        ) : (
                          <div className="w-7 flex-shrink-0" aria-hidden="true" />
                        )
                      )}
                      <div className={`max-w-[78%] flex flex-col ${mine ? "items-end" : "items-start"} gap-1`}>
                        {m.attachments && m.attachments.length > 0 && (
                          <div className="flex flex-col gap-1.5">
                            {m.attachments.map((a, idx) => <AttachmentPreview key={idx} a={a} mine={mine} />)}
                          </div>
                        )}
                        {m.content && (
                          <div className={`bubble ${mine ? "bubble-sent" : "bubble-received"}`}>
                            <div className="text-sm whitespace-pre-wrap break-words leading-relaxed">{m.content}</div>
                          </div>
                        )}
                        <div className={`bubble-meta ${mine ? "self-end" : "self-start"}`}>
                          {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                          {mine && i === messages.length - 1 && <span className="ml-1 text-pitch">✓✓</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Composer — pinned to bottom via flex-shrink-0 */}
              {pendingFiles.length > 0 && (
                <div className="flex-shrink-0 border-t border-white/8 px-3 py-2 flex gap-2 overflow-x-auto scroll-thin bg-black/30">
                  {pendingFiles.map((p, idx) => {
                    const Icon = p.kind === "image" ? ImageIcon : p.kind === "video" ? Film : FileText;
                    return (
                      <div key={idx} className="relative border border-pitch/30 bg-pitch/5 px-2.5 py-1.5 flex items-center gap-2 flex-shrink-0 rounded">
                        <Icon className="w-3.5 h-3.5 text-pitch" />
                        <span className="text-[11px] max-w-[140px] truncate font-mono">{p.name}</span>
                        <button type="button" onClick={() => removePending(idx)} className="text-white/40 hover:text-red-400 ml-1"><X className="w-3 h-3" /></button>
                      </div>
                    );
                  })}
                </div>
              )}

              <form onSubmit={send} className="flex-shrink-0 px-3 py-3 border-t border-white/10 flex gap-2 items-end bg-black/40 backdrop-blur-md">
                <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading || active.status === "pending" || active.status === "rejected"} className="p-2.5 border border-white/10 hover:border-pitch hover:bg-pitch/5 transition-all rounded-full flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed" data-testid="chat-attach" title={user?.role === "coach" && !user?.is_pro ? "Pro for video/PDF" : ""}>
                  {uploading ? <Sparkles className="w-4 h-4 animate-pulse text-pitch" /> : <Paperclip className="w-4 h-4" />}
                </button>
                {active.status !== "pending" && active.status !== "rejected" && (
                  <VoiceRecorder
                    onResult={async (att) => {
                      await api.post(`/chat/threads/${active.id}/messages`, {
                        thread_id: active.id,
                        content: "",
                        attachments: [att],
                      }).then(({ data }) => {
                        setMessages((prev) => prev.find((m) => m.id === data.id) ? prev : [...prev, data]);
                      });
                    }}
                  />
                )}
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*,video/*,application/pdf"
                  multiple
                  className="hidden"
                  onChange={handleFile}
                  data-testid="chat-attach-input"
                />
                <input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder={active.status === "pending" ? (lang === "bg" ? "Чатът е заключен — изчаква одобрение" : "Chat locked — awaiting approval") : active.status === "rejected" ? (lang === "bg" ? "Заявката беше отказана" : "Request was declined") : t("typeMessage")}
                  disabled={active.status === "pending" || active.status === "rejected"}
                  className="flex-1 bg-[#0A1628] border border-white/10 px-4 py-2.5 focus:border-pitch outline-none rounded-full text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="chat-input"
                />
                <button type="submit" disabled={(!text.trim() && pendingFiles.length === 0) || active.status === "pending" || active.status === "rejected"} className="btn-primary rounded-full w-11 h-11 flex items-center justify-center disabled:opacity-40 flex-shrink-0" data-testid="chat-send" aria-label="Send"><Send className="w-4 h-4" /></button>
              </form>
              {user?.role === "coach" && !user?.is_pro && (
                <div className="flex-shrink-0 px-4 py-1.5 text-[10px] font-mono tracking-widest text-white/30 border-t border-white/5 flex items-center gap-1.5">
                  <Lock className="w-3 h-3" /> {lang === "bg" ? "PRO за видео/PDF — само снимки в Free" : "PRO for video/PDF — images only on Free"}
                </div>
              )}
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-white/40 p-6 text-center">
              <div className="w-16 h-16 rounded-full bg-pitch/10 border border-pitch/30 flex items-center justify-center mb-4">
                <Send className="w-7 h-7 text-pitch" />
              </div>
              <div className="font-oswald uppercase text-xl tracking-tight mb-1">{lang === "bg" ? "Избери разговор" : "Select a conversation"}</div>
              <div className="text-[11px] font-mono tracking-widest text-white/35">{lang === "bg" ? "или започни нов с + бутона" : "or start a new one with the + button"}</div>
            </div>
          )}
        </section>
      </div>

      {showNew && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowNew(false)}>
          <div className="bg-[#0A1628] border border-white/10 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-white/10">
              <h2 className="font-oswald uppercase text-xl">{t("selectUser")}</h2>
              <button onClick={() => setShowNew(false)}><X className="w-5 h-5" /></button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto">
              {users.length === 0 ? (
                <div className="p-8 text-center text-white/30 text-xs font-mono">—</div>
              ) : users.map((u) => (
                <button key={u.id} onClick={() => createThread(u.id)} className="w-full text-left p-4 border-b border-white/5 hover:bg-white/5 flex items-center gap-3" data-testid={`user-${u.id}`}>
                  <div className="w-10 h-10 bg-pitch text-white font-oswald flex items-center justify-center">{u.name?.[0]}</div>
                  <div>
                    <div className="font-oswald uppercase tracking-tight">{u.name}</div>
                    <div className="text-xs text-white/40">{u.email}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
